/**
 * V2PaywallBodySection — V2-native editor section for paywall body content
 *
 * Edits: Title, Subtitle (copy block) + Bullet features (options)
 * V2 paths:
 * - content.copy.title (title)
 * - content.copy.subtitle (subtitle)
 * - content.options.items (bullet features)
 *
 * Replaces: OnboardingPaywallBodySection (for V2 context)
 * Reuses: V2TemplateTextElement, BulletItemEditor, BulletStyleSections, SortableList
 *
 * Key V2 differences:
 * - Text: translations.{locale} (was l10n.{locale})
 * - Styles: appearance.* (was styles.*)
 * - Image: translations.{locale} (was l10n.{locale}.assetUrl)
 * - Bullet items path: content.options.items (was list.items)
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import React, { useCallback, useMemo, useState, useRef } from 'react';
import { Box, Button, IconButton, Collapse, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import { SAccordion, SortableList } from '@modules/ui-components';
import { OnboardingMediaUploadService } from '@modules/firebase-core/services/OnboardingMediaUploadService';
import { toV2AssetReference, readAssetUrl, type V2AssetReference } from '@modules/reactFlow/utils/v2AssetUtils';
import { V2TemplateTextElement } from '../../shared/templateFields/V2TemplateTextElement';
import { EditorGuideProvider } from '../../../contexts';
import { buildFlowVariableItems } from '../../../config/flowVariablesConfig';
import { variableResolver } from '@modules/v2-variable-utils';
import type { FlowDocument } from '@segmently/schema-models/v2';
import { SECTION_MARGIN_BOTTOM, SPACING } from '../../../config/ui';
import {
  expandedToggleButtonSx,
  expandedToggleButtonActiveSx,
} from '../../../config/editorFieldStyles';
import {
  V2_PAYWALL_TITLE_CONFIG,
  V2_PAYWALL_SUBTITLE_CONFIG,
  V2_PAYWALL_FEATURES_ARRAY_CONFIG,
  V2_BULLET_STYLE_SECTIONS,
  createDefaultV2FeatureItem,
} from '../../../config/v2PaywallBodyConfig';
import { withScreenEditorFieldGuide } from '../../../config/screenEditorFieldGuideResolver';
import { getValueByPath, isFieldEnabled } from '../../../utils/v2FieldOperations';
import { getStyleValues as getStyleValuesUtil } from '../../../utils/fieldValueOperations';
import { getSectionTestId, getInputTestId, getCheckboxTestId, getStylesToggleTestId, getFieldTestId } from '../../../utils/testIdUtils';
import {
  cloneTemplateItem,
  resetAssetTranslations,
  resetLocalizedLabelContent,
} from '@modules/reactFlow/utils/v2ItemInheritance';
import { BulletItemEditor, type BulletItemData } from './paywall/BulletItemEditor';
import { BulletStyleSections } from './paywall/BulletStyleSections';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { FieldConfig } from '../../../types/editorFieldTypes';

export interface V2PaywallBodySectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  projectId?: string;
  onboardingId?: string;
  versionId?: string;
  /** Full FlowDocument for BFS-based variable resolution in @variable autocomplete */
  flowDocument?: FlowDocument | null;
}

/**
 * V2 feature item structure (OptionsItem)
 */
interface V2FeatureItem {
  title: {
    translations: Record<string, string>;
    appearance?: Record<string, unknown>;
    layout?: { insets: Record<string, unknown> };
  };
  subtitle?: {
    translations: Record<string, string>;
    appearance?: Record<string, unknown>;
    layout?: { insets: Record<string, unknown> };
  };
  image?: {
    translations: Record<string, unknown>;
    appearance?: Record<string, unknown>;
    layout?: { insets: Record<string, unknown> };
  } | null;
}

function getV2LabelValue(labelObj: unknown, locale: string): string {
  if (!labelObj || typeof labelObj !== 'object') return '';
  const label = labelObj as Record<string, unknown>;
  const translations = label.translations as Record<string, string> | undefined;
  if (!translations) return '';
  return translations[locale] || '';
}

function getV2ImageUrl(item: unknown, locale: string): string {
  if (!item || typeof item !== 'object') return '';
  const feature = item as V2FeatureItem;
  const imageTranslations = feature.image?.translations?.[locale];
  if (!imageTranslations || typeof imageTranslations !== 'object') return '';
  return readAssetUrl(imageTranslations as Record<string, string>) ?? '';
}

function transformToEditableItems(
  items: unknown[],
  locale: string
): (BulletItemData & { originalIndex: number })[] {
  return items.map((item, index) => {
    const itemObj = item as V2FeatureItem;
    return {
      id: `bullet-${index}`,
      originalIndex: index,
      imageUrl: getV2ImageUrl(item, locale),
      title: getV2LabelValue(itemObj.title, locale),
      subtitle: getV2LabelValue(itemObj.subtitle, locale),
    };
  });
}

export const V2PaywallBodySection: React.FC<V2PaywallBodySectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  projectId,
  onboardingId,
  versionId,
  flowDocument,
}) => {
  const uploadService = useMemo(() => new OnboardingMediaUploadService(), []);
  const lastUploadedAssetUrlRef = useRef<V2AssetReference | null>(null);
  const [bulletStylesExpanded, setBulletStylesExpanded] = useState(false);
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  // BFS-scoped flow variables for @variable autocomplete in title/subtitle.
  // Mirrors V2CopyBlockSection — only variables reachable from this screen via predecessors.
  const flowVariableItems = useMemo(() => {
    if (!flowDocument || !step.id) return [];
    const boundVars = variableResolver.findAvailableVariables(flowDocument, step.id);
    const varsRecord: Record<string, import('@modules/v2-variable-utils').FunnelVariable> = {};
    for (const bv of boundVars) {
      varsRecord[bv.variable.id] = bv.variable;
    }
    return buildFlowVariableItems(varsRecord);
  }, [flowDocument, step.id]);

  // ── Text field helpers ──
  const isEnabled = useCallback(
    (config: FieldConfig): boolean => isFieldEnabled(step, config.path),
    [step]
  );

  const getStyleValues = useCallback(
    (config: FieldConfig): Record<string, unknown> => {
      const result = getStyleValuesUtil(step as unknown as Record<string, unknown>, config);
      // V2 EdgeInsets uses top/bottom/start/end, translate to paddingTop/Right/Bottom/Left for PaddingElement
      if (config.paddingConfig) {
        const bp = config.paddingConfig.basePath;
        const insets = getValueByPath<Record<string, unknown>>(step, bp);
        if (insets && typeof insets === 'object') {
          result[`${bp}.paddingTop`] = insets.top ?? result[`${bp}.paddingTop`];
          result[`${bp}.paddingRight`] = insets.end ?? result[`${bp}.paddingRight`];
          result[`${bp}.paddingBottom`] = insets.bottom ?? result[`${bp}.paddingBottom`];
          result[`${bp}.paddingLeft`] = insets.start ?? result[`${bp}.paddingLeft`];
        }
      }
      return result;
    },
    [step]
  );

  const handleEnabledChange = useCallback(
    (config: FieldConfig, enabled: boolean) => {
      if (enabled) {
        const defaultValue = {
          translations: { [locale]: config.defaultValue || '' },
          kind: 'static',
          templateParams: { labels: {}, links: {}, countdowns: {} },
          appearance: {},
          layout: { insets: {} },
        };
        onChange(config.path, defaultValue);
      } else {
        onChange(config.path, null);
      }
    },
    [locale, onChange]
  );

  const handleStyleChange = useCallback(
    (path: string, value: unknown) => {
      // Translate V1 padding paths to V2 EdgeInsets
      const translatedPath = path
        .replace('.paddingTop', '.top')
        .replace('.paddingRight', '.end')
        .replace('.paddingBottom', '.bottom')
        .replace('.paddingLeft', '.start');
      onChange(translatedPath, value);
    },
    [onChange]
  );

  const toggleFieldExpansion = useCallback((path: string) => {
    setExpandedFields(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  // ── Bullet features ──
  const rawFeatures = useMemo(() => {
    const items = getValueByPath<unknown[]>(step, V2_PAYWALL_FEATURES_ARRAY_CONFIG.path);
    return Array.isArray(items) ? items : [];
  }, [step]);

  const editableBulletItems = useMemo(
    () => transformToEditableItems(rawFeatures, locale),
    [rawFeatures, locale]
  );

  const handleBulletStylesClick = useCallback(() => {
    setBulletStylesExpanded((prev) => !prev);
  }, []);

  const handleBulletItemChange = useCallback(
    (index: number, field: keyof BulletItemData, value: unknown) => {
      const freshItems = getValueByPath<unknown[]>(step, V2_PAYWALL_FEATURES_ARRAY_CONFIG.path) || [];
      const newItems = [...freshItems];
      const item = { ...(newItems[index] as V2FeatureItem) };

      if (field === 'title') {
        const existingTitle = item.title || {
          translations: {},
          kind: 'static',
          parameters: { labels: {}, links: {} },
          appearance: {},
          layout: { insets: {} },
        };
        item.title = {
          ...existingTitle,
          translations: { ...existingTitle.translations, [locale]: value as string },
        };
      } else if (field === 'subtitle') {
        const existingSubtitle = item.subtitle || {
          translations: {},
          kind: 'static',
          parameters: { labels: {}, links: {} },
          appearance: {},
          layout: { insets: {} },
        };
        item.subtitle = {
          ...existingSubtitle,
          translations: { ...existingSubtitle.translations, [locale]: value as string },
        };
      } else if (field === 'imageUrl') {
        const imageUrl = value as string;
        if (imageUrl) {
          const assetUrl = lastUploadedAssetUrlRef.current ?? { original: imageUrl, small: imageUrl };
          item.image = {
            translations: { [locale]: assetUrl },
            appearance: {},
            layout: { insets: {} },
          };
          lastUploadedAssetUrlRef.current = null;
        } else {
          item.image = null;
        }
      }

      newItems[index] = item;
      onChange(V2_PAYWALL_FEATURES_ARRAY_CONFIG.path, newItems);
    },
    [step, locale, onChange]
  );

  const handleAddBulletItem = useCallback(() => {
    const freshItems = getValueByPath<unknown[]>(step, V2_PAYWALL_FEATURES_ARRAY_CONFIG.path) || [];
    const newItem = cloneTemplateItem<Record<string, unknown>>(freshItems) ?? createDefaultV2FeatureItem(locale);
    newItem.title = resetLocalizedLabelContent(newItem.title, locale);
    newItem.subtitle = resetLocalizedLabelContent(newItem.subtitle, locale);
    if (newItem.image !== null) {
      newItem.image = resetAssetTranslations(newItem.image);
    }

    const newItems = [...freshItems, newItem];
    onChange(V2_PAYWALL_FEATURES_ARRAY_CONFIG.path, newItems);
  }, [step, locale, onChange]);

  const handleDeleteBulletItem = useCallback(
    (index: number) => {
      const freshItems = getValueByPath<unknown[]>(step, V2_PAYWALL_FEATURES_ARRAY_CONFIG.path) || [];
      const newItems = freshItems.filter((_, i) => i !== index);
      onChange(V2_PAYWALL_FEATURES_ARRAY_CONFIG.path, newItems);
    },
    [step, onChange]
  );

  const handleReorderBulletItems = useCallback(
    (reorderedItems: (BulletItemData & { originalIndex: number })[]) => {
      const freshItems = getValueByPath<unknown[]>(step, V2_PAYWALL_FEATURES_ARRAY_CONFIG.path) || [];
      const newItems = reorderedItems.map((item) => freshItems[item.originalIndex]);
      onChange(V2_PAYWALL_FEATURES_ARRAY_CONFIG.path, newItems);
    },
    [step, onChange]
  );

  const handleBulletImageUpload = useCallback(
    async (file: File): Promise<string> => {
      if (!projectId || !onboardingId || !versionId) {
        throw new Error('Upload context not configured');
      }
      const result = await uploadService.uploadImage(file, {
        projectId, onboardingId, versionId, locale,
      });
      lastUploadedAssetUrlRef.current = toV2AssetReference(result.assetUrl);
      return result.assetUrl.origin;
    },
    [uploadService, projectId, onboardingId, versionId, locale]
  );

  const SECTION_NAME = 'Content';

  // Build per-field props for V2TemplateTextElement (mirrors V2CopyBlockSection pattern)
  const titleEnabled = isEnabled(V2_PAYWALL_TITLE_CONFIG);
  const titleStyleValues = getStyleValues(V2_PAYWALL_TITLE_CONFIG);
  const titleExpanded = expandedFields.has(V2_PAYWALL_TITLE_CONFIG.path);

  const subtitleEnabled = isEnabled(V2_PAYWALL_SUBTITLE_CONFIG);
  const subtitleStyleValues = getStyleValues(V2_PAYWALL_SUBTITLE_CONFIG);
  const subtitleExpanded = expandedFields.has(V2_PAYWALL_SUBTITLE_CONFIG.path);

  return (
    <EditorGuideProvider>
      <SAccordion
        title={SECTION_NAME}
        collapsible
        defaultExpanded={defaultExpanded}
        sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
        data-testid={getSectionTestId('paywall-body')}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Title — V2TemplateTextElement (Tiptap) supports multiline + @variable autocomplete */}
          <V2TemplateTextElement
            step={step}
            fieldConfig={withScreenEditorFieldGuide(V2_PAYWALL_TITLE_CONFIG, 'paywallBody')}
            locale={locale}
            onChange={onChange}
            enabled={titleEnabled}
            styleValues={titleStyleValues}
            expandedStyles={titleExpanded}
            onEnabledChange={(enabled) => handleEnabledChange(V2_PAYWALL_TITLE_CONFIG, enabled)}
            onStylesExpandToggle={() => toggleFieldExpansion(V2_PAYWALL_TITLE_CONFIG.path)}
            onStyleChange={handleStyleChange}
            disabled={disabled}
            screenType={step.screenType}
            flowVariableItems={flowVariableItems}
            data-testid={getFieldTestId(SECTION_NAME, V2_PAYWALL_TITLE_CONFIG.label)}
            checkboxTestId={getCheckboxTestId(SECTION_NAME, V2_PAYWALL_TITLE_CONFIG.label)}
            stylesToggleTestId={getStylesToggleTestId(SECTION_NAME, V2_PAYWALL_TITLE_CONFIG.label)}
            inputTestId={getInputTestId(SECTION_NAME, V2_PAYWALL_TITLE_CONFIG.label)}
            styleGroupTestId="styles-group-content-title"
          />

          {/* Subtitle — V2TemplateTextElement (Tiptap) supports multiline + @variable autocomplete */}
          <V2TemplateTextElement
            step={step}
            fieldConfig={withScreenEditorFieldGuide(V2_PAYWALL_SUBTITLE_CONFIG, 'paywallBody')}
            locale={locale}
            onChange={onChange}
            enabled={subtitleEnabled}
            styleValues={subtitleStyleValues}
            expandedStyles={subtitleExpanded}
            onEnabledChange={(enabled) => handleEnabledChange(V2_PAYWALL_SUBTITLE_CONFIG, enabled)}
            onStylesExpandToggle={() => toggleFieldExpansion(V2_PAYWALL_SUBTITLE_CONFIG.path)}
            onStyleChange={handleStyleChange}
            disabled={disabled}
            screenType={step.screenType}
            flowVariableItems={flowVariableItems}
            data-testid={getFieldTestId(SECTION_NAME, V2_PAYWALL_SUBTITLE_CONFIG.label)}
            checkboxTestId={getCheckboxTestId(SECTION_NAME, V2_PAYWALL_SUBTITLE_CONFIG.label)}
            stylesToggleTestId={getStylesToggleTestId(SECTION_NAME, V2_PAYWALL_SUBTITLE_CONFIG.label)}
            inputTestId={getInputTestId(SECTION_NAME, V2_PAYWALL_SUBTITLE_CONFIG.label)}
            styleGroupTestId="styles-group-content-subtitle"
          />

          {/* Bullets Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: SPACING.md }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography fontWeight={500} fontSize="1rem">
                Bullets
              </Typography>
              <IconButton
                size="small"
                onClick={handleBulletStylesClick}
                disabled={disabled}
                data-testid="bullet-style-settings-button"
                sx={bulletStylesExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </Box>

            <Collapse in={bulletStylesExpanded} timeout="auto" unmountOnExit>
              <BulletStyleSections
                sections={V2_BULLET_STYLE_SECTIONS}
                struct={step as unknown as Record<string, unknown>}
                onChange={onChange}
                disabled={disabled}
                itemsPath="content.options.items"
                useV2Insets
              />
            </Collapse>

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddBulletItem}
              disabled={disabled}
              data-testid="add-bullet-item"
              sx={{ alignSelf: 'flex-end' }}
            >
              Add Bullet
            </Button>

            {editableBulletItems.length > 0 ? (
              <SortableList
                items={editableBulletItems}
                onReorder={handleReorderBulletItems}
                gap="sm"
                disabled={disabled}
              >
                {(item, index) => (
                  <SortableList.Item
                    key={item.id}
                    showDragHandle
                    dragHandlePosition="start"
                    disabled={disabled}
                  >
                    <BulletItemEditor
                      index={index}
                      item={item}
                      onChange={(field, value) => handleBulletItemChange(item.originalIndex, field, value)}
                      onImageUpload={handleBulletImageUpload}
                      onDelete={() => handleDeleteBulletItem(item.originalIndex)}
                      canDelete={editableBulletItems.length > 1}
                      disabled={disabled}
                    />
                  </SortableList.Item>
                )}
              </SortableList>
            ) : (
              <Box
                sx={{
                  py: 4,
                  textAlign: 'center',
                  color: 'text.secondary',
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                No bullets. Click "Add Bullet" to create one.
              </Box>
            )}
          </Box>
        </Box>
      </SAccordion>
    </EditorGuideProvider>
  );
};

export default V2PaywallBodySection;
